import {
  AppTrackingTransparency,
  AppTrackingStatus,
} from 'capacitor-plugin-app-tracking-transparency';
import { useState } from 'react';
import { useMount } from 'react-use';
import { usePlatform } from './usePlatform';

interface UseIosTrackingPermissionReturn {
  status: AppTrackingStatus | null;
  isLoading: boolean;
  requestPermission: () => Promise<AppTrackingStatus>;
}

export const useIosTrackingPermission = (): UseIosTrackingPermissionReturn => {
  const { isIOS } = usePlatform();
  const [status, setStatus] = useState<AppTrackingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getStatus = async () => {
    try {
      const response = await AppTrackingTransparency.getStatus();
      return response.status;
    } catch (error) {
      console.error('Error getting iOS tracking status:', error);
      return 'notDetermined';
    }
  };

  const requestPermission = async () => {
    if (!isIOS) {
      return 'notDetermined';
    }

    setIsLoading(true);
    try {
      const response = await AppTrackingTransparency.requestPermission();
      setStatus(response.status);
      return response.status;
    } catch (error) {
      console.error('Error requesting iOS tracking permission:', error);
      const fallbackStatus = 'denied';
      setStatus(fallbackStatus);
      return fallbackStatus;
    } finally {
      setIsLoading(false);
    }
  };

  useMount(() => {
    if (!isIOS) {
      return;
    }

    const checkInitialStatus = async () => {
      setIsLoading(true);
      const initialStatus = await getStatus();
      setStatus(initialStatus);

      if (initialStatus === 'notDetermined') {
        await requestPermission();
      }
      setIsLoading(false);
    };

    void checkInitialStatus();
  });

  return {
    status,
    isLoading,
    requestPermission,
  };
};
